import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { JobFilters, type FilterType } from '../components/JobFilters';
import { JobForm } from '../components/JobForm';
import { JobStatusControl } from '../components/JobStatusControl';
import { useJobs } from '../hooks/useJobs';
import { useReferenceData } from '@/features/leads/hooks/useReferenceData';
import { getJob } from '../api/jobs-api';
import type { JobDetailDto, JobsQueryParams, JobStatus } from '../types';

export function JobsPage() {
  const navigate = useNavigate();
  const { id: urlJobId } = useParams<{ id: string }>();
  const [queryParams, setQueryParams] = useState<JobsQueryParams>({
    status: 'Active',
  });
  const [currentFilter, setCurrentFilter] = useState<FilterType>('all-active');
  const [selectedJob, setSelectedJob] = useState<JobDetailDto | null>(null);

  // Initialize selectedJobId from URL param if present
  const initialJobId = urlJobId ? parseInt(urlJobId, 10) : undefined;
  const [selectedJobId, setSelectedJobId] = useState<number | undefined>(
    initialJobId && !isNaN(initialJobId) ? initialJobId : undefined
  );

  // Track if we've loaded the initial URL job
  const hasLoadedUrlJob = useRef(false);

  const { data: jobs, isLoading, error, refetch } = useJobs(queryParams);
  const { users } = useReferenceData();

  const loadJob = useCallback(async (jobId: number) => {
    try {
      const fullJob = await getJob(jobId);
      setSelectedJob(fullJob);
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      alert(error.response?.data?.message || 'Failed to load job details');
    }
  }, []);

  // Load job from URL param if provided (only on initial mount)
  useEffect(() => {
    if (urlJobId && !hasLoadedUrlJob.current) {
      const jobId = parseInt(urlJobId, 10);
      if (!isNaN(jobId)) {
        hasLoadedUrlJob.current = true;
        // Use setTimeout to avoid synchronous setState in effect
        setTimeout(() => loadJob(jobId), 0);
      }
    }
  }, [urlJobId, loadJob]);

  const handleJobSelect = (jobId: number | undefined) => {
    setSelectedJobId(jobId);
    if (jobId) {
      loadJob(jobId);
    } else {
      setSelectedJob(null);
    }
  };

  const handleFilterChange = (filter: FilterType) => {
    setCurrentFilter(filter);

    // Update query params based on filter
    switch (filter) {
      case 'all-active':
        setQueryParams({
          status: 'Active',
        });
        break;
      case 'closed':
        setQueryParams({
          status: 'Closed',
        });
        break;
      case 'collections':
        setQueryParams({
          status: 'Collections',
        });
        break;
      case 'all':
        setQueryParams({
          status: undefined,
        });
        break;
      case 'by-estimator':
        // Already handled by onAssignedToChange
        break;
    }
  };

  const handleAssignedToChange = (userId: number | undefined) => {
    setQueryParams((prev) => ({
      ...prev,
      assignedToId: userId,
    }));
  };

  const handleFormSuccess = () => {
    refetch();
    if (selectedJobId) {
      loadJob(selectedJobId);
    }
  };

  const handleViewLead = () => {
    if (selectedJob) {
      navigate(`/leads/${selectedJob.leadId}`);
    }
  };

  const handleStatusChanged = (newStatus: JobStatus) => {
    refetch();
    if (selectedJobId) {
      loadJob(selectedJobId);
    }
    alert(`Job status changed to ${newStatus}`);
  };

  return (
    <div className="min-h-screen bg-[#f6f6f6] px-5 py-6 space-y-4">
      {/* Main Content Container */}
      <div className="bg-[#f6f6f6] rounded-[10px] p-5 space-y-4">
        {/* Job Management Header Section */}
        <div className="space-y-3">
          {/* Job Management Title */}
          <h2 className="text-lg font-semibold text-[#0a0a9e]">Job Management</h2>

          {/* Selected Job and Action Buttons Row */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Selected Job Dropdown */}
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-semibold text-black">Selected Job</span>
              <div className="w-[350px]">
                <Select
                  value={selectedJobId?.toString() || ''}
                  onChange={(e) => handleJobSelect(e.target.value ? Number(e.target.value) : undefined)}
                  className="h-8 rounded-[20px] border-[1.5px] border-[#d9d9d9] px-3 text-sm font-medium"
                >
                  <option value="">Select Job</option>
                  {jobs.map((job) => (
                    <option key={job.id} value={job.id}>
                      {job.jobNumber} - {job.leadTitle}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            {/* Status Control */}
            {selectedJob && (
              <JobStatusControl
                jobId={selectedJob.id}
                currentStatus={selectedJob.status}
                onStatusChanged={handleStatusChanged}
              />
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-1.5 ml-auto">
              <Button
                onClick={handleViewLead}
                disabled={!selectedJob}
                variant="ghost"
                className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-black hover:bg-gray-50 disabled:opacity-50"
              >
                <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 8V24M8 16H24" stroke="#d71f20" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                View Lead
              </Button>

              <Button
                variant="ghost"
                className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-black hover:bg-gray-50"
                onClick={() => navigate('/jobs/search')}
              >
                <svg width="25" height="23" viewBox="0 0 33 31" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="13" cy="13" r="10" stroke="#d71f20" strokeWidth="2" fill="none"/>
                  <path d="M20 20L30 30" stroke="#d71f20" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Search
              </Button>
            </div>
          </div>
        </div>

        {/* Filter Pills Row */}
        <div className="flex items-center gap-1.5">
          <JobFilters
            onFilterChange={handleFilterChange}
            onAssignedToChange={handleAssignedToChange}
            currentFilter={currentFilter}
            users={users}
            selectedAssignedToId={queryParams.assignedToId}
          />
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#D71F20] mx-auto"></div>
          <p className="mt-3 text-[#84878A] text-sm">Loading jobs...</p>
        </div>
      )}

      <ErrorAlert error={error} className="rounded-[20px] p-2" />

      {/* Job Form - Inline Display */}
      {!isLoading && !error && selectedJob && (
        <JobForm
          job={selectedJob}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Empty State */}
      {!isLoading && !error && !selectedJob && jobs.length > 0 && (
        <div className="text-center py-8">
          <p className="text-[#84878A] text-sm">Select a job from the dropdown above to view details</p>
        </div>
      )}

      {/* No Jobs State */}
      {!isLoading && !error && jobs.length === 0 && (
        <div className="text-center py-8">
          <p className="text-[#84878A] text-sm">No jobs found matching the current filter</p>
        </div>
      )}
    </div>
  );
}
